/**
 * Sprint BG Part 3 — `@citizen-walk/shared-i18n` entry point。
 *
 * 設計:
 *   - 軽量 key-based translator (依存なし、外部 i18n lib は次 sprint で必要に応じて検討)
 *   - locale module-scoped (singleton)。SSR / 多 tab 対応は後続
 *   - `t(key, params?)` で `{name}` 形式の interpolation
 *   - key が dict に無い場合は key を素通し (fail-soft、UI で空文字を見せない)
 *   - ja fallback: en で未翻訳 key は ja から引く (実運用では稀、t() を呼んだ瞬間に key だけ
 *     表示されると UX 劣化するため)
 *
 * 使い方:
 *   import { t, setLocale } from '@citizen-walk/shared-i18n';
 *   setLocale('en');
 *   t('post.submit_button'); // → 'Post'
 *   t('auth.nickname_placeholder');
 */

import { DICTIONARIES } from './dictionaries';
import type { I18nKey, Locale } from './keys';

export type { I18nKey, Locale };
export { I18N_KEYS } from './keys';

const DEFAULT_LOCALE: Locale = 'ja';
let currentLocale: Locale = DEFAULT_LOCALE;

/** 現在の locale を取得。 */
export function getLocale(): Locale {
  return currentLocale;
}

/** locale を切り替える。未知 locale は no-op。 */
export function setLocale(locale: Locale): void {
  if (locale === 'ja' || locale === 'en') {
    currentLocale = locale;
  }
}

/**
 * key を現在の locale で翻訳する。`{param}` 形式の placeholder を `params` で置換。
 *
 * - key が dict に無ければ key 文字列を素通し (fail-soft)
 * - en で空文字なら ja から fallback
 */
export function t(key: I18nKey, params?: Record<string, string | number>): string {
  const dict = DICTIONARIES[currentLocale];
  let raw = dict[key];
  if (!raw && currentLocale !== DEFAULT_LOCALE) {
    raw = DICTIONARIES[DEFAULT_LOCALE][key];
  }
  if (!raw) return key;
  if (!params) return raw;
  return raw.replace(/\{(\w+)\}/g, (_, name: string) => {
    const v = params[name];
    return v === undefined ? `{${name}}` : String(v);
  });
}
